(() => {
  'use strict';

  const MAIN_STORAGE_KEY = 'zlnDesignLaborStateV1';
  const TYPOGRAPHY_STORAGE_KEY =
    'zlnDesignLaborTypographyStateV1';

  const INITIALIZED_ATTRIBUTE =
    'data-typography-initialized';

  const FONT_LINK_PREFIX =
    'zln-labor-fonts-';

  function findCurrentScript() {
    if (document.currentScript) {
      return document.currentScript;
    }

    const scripts =
      Array.from(document.scripts);

    return (
      scripts.find((script) => {
        return script.src.includes(
          'zln-labor-typography.js'
        );
      }) || null
    );
  }

  function ensureTypographyData() {
    if (
      window.ZLN_LABOR_TYPOGRAPHY_DATA
    ) {
      initializeAll();
      return;
    }

    const currentScript =
      findCurrentScript();

    if (
      !currentScript ||
      !currentScript.src
    ) {
      document.addEventListener(
        'zln:labor-typography-data-ready',
        initializeAll,
        {
          once: true
        }
      );

      return;
    }

    const existingDataScript =
      Array.from(document.scripts).find(
        (script) => {
          return script.src.includes(
            'zln-labor-typography-data.js'
          );
        }
      );

    if (existingDataScript) {
      existingDataScript.addEventListener(
        'load',
        initializeAll,
        {
          once: true
        }
      );

      document.addEventListener(
        'zln:labor-typography-data-ready',
        initializeAll,
        {
          once: true
        }
      );

      return;
    }

    const dataScript =
      document.createElement('script');

    dataScript.src =
      currentScript.src.replace(
        'zln-labor-typography.js',
        'zln-labor-typography-data.js'
      );

    dataScript.async = false;

    dataScript.addEventListener(
      'load',
      initializeAll,
      {
        once: true
      }
    );

    dataScript.addEventListener(
      'error',
      () => {
        console.error(
          '[ZLN Design Labor] A tipográfiai adatfájl nem tölthető be.'
        );
      }
    );

    document.head.appendChild(
      dataScript
    );
  }

  function safeParse(
    value,
    fallback
  ) {
    try {
      return value
        ? JSON.parse(value)
        : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function readStorage(
    key,
    fallback = {}
  ) {
    try {
      return safeParse(
        window.localStorage.getItem(key),
        fallback
      );
    } catch (error) {
      return fallback;
    }
  }

  function writeStorage(
    key,
    value
  ) {
    try {
      window.localStorage.setItem(
        key,
        JSON.stringify(value)
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  function clone(value) {
    return JSON.parse(
      JSON.stringify(value)
    );
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function numberValue(
    value,
    fallback
  ) {
    const parsed =
      Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  }

  function formatEm(value) {
    const number =
      numberValue(value, 0);

    const formatted =
      Number(number.toFixed(3));

    return `${formatted}em`;
  }

  function formatLineHeight(value) {
    const number =
      numberValue(value, 1);

    return Number(
      number.toFixed(2)
    ).toString();
  }

  function getMainState() {
    return readStorage(
      MAIN_STORAGE_KEY,
      {}
    );
  }

  function getSelectedColors() {
    const mainState =
      getMainState();

    const colors =
      mainState.colors || {};

    return {
      background:
        colors.background ||
        '#F1F1EB',

      surface:
        colors.surface ||
        '#FFFFFF',

      text:
        colors.text ||
        '#11140F',

      primary:
        colors.primary ||
        '#BDF35A',

      secondary:
        colors.secondary ||
        '#BCA8FF',

      paletteName:
        colors.paletteName ||
        'Alap ZLN színrendszer'
    };
  }

  function createDefaultState(data) {
    return {
      version: 1,
      typographyPage: 0,
      typographyMode: 'presets',
      typography: null,
      customTypography: clone(
        data.customDefaults
      ),
      updatedAt: null
    };
  }

  function loadTypographyState(data) {
    const defaults =
      createDefaultState(data);

    const separateState =
      readStorage(
        TYPOGRAPHY_STORAGE_KEY,
        {}
      );

    const mainState =
      getMainState();

    const typography =
      separateState.typography ||
      mainState.typography ||
      null;

    return {
      ...defaults,
      ...separateState,
      typography,

      customTypography: {
        ...defaults.customTypography,
        ...(mainState.customTypography || {}),
        ...(separateState.customTypography || {})
      }
    };
  }

  function mergeTypographyIntoMain(
    state
  ) {
    const mainState =
      getMainState();

    const merged = {
      ...mainState,

      typography:
        state.typography,

      customTypography:
        state.customTypography,

      typographyPage:
        state.typographyPage,

      typographyMode:
        state.typographyMode,

      updatedAt:
        new Date().toISOString()
    };

    writeStorage(
      MAIN_STORAGE_KEY,
      merged
    );
  }

  function saveTypographyState(
    state
  ) {
    state.updatedAt =
      new Date().toISOString();

    writeStorage(
      TYPOGRAPHY_STORAGE_KEY,
      state
    );

    mergeTypographyIntoMain(
      state
    );
  }

  function resolveSystem(
    data,
    system,
    category
  ) {
    const headingFont =
      data.fontCatalog[
        system.headingFont
      ];

    const bodyFont =
      data.fontCatalog[
        system.bodyFont
      ];

    return {
      mode: 'preset',

      systemId:
        system.id,

      systemName:
        system.name,

      categoryId:
        category.id,

      category:
        category.title,

      description:
        system.description,

      headingFont:
        system.headingFont,

      headingFontLabel:
        headingFont?.label ||
        system.headingFont,

      headingFontFamily:
        headingFont?.family ||
        'Arial, sans-serif',

      bodyFont:
        system.bodyFont,

      bodyFontLabel:
        bodyFont?.label ||
        system.bodyFont,

      bodyFontFamily:
        bodyFont?.family ||
        'Arial, sans-serif',

      headingWeight:
        system.headingWeight,

      bodyWeight:
        system.bodyWeight,

      headingLetterSpacing:
        system.headingLetterSpacing,

      headingLineHeight:
        system.headingLineHeight,

      bodyLetterSpacing:
        system.bodyLetterSpacing,

      bodyLineHeight:
        system.bodyLineHeight,

      metaWeight:
        system.metaWeight,

      metaLetterSpacing:
        system.metaLetterSpacing,

      metaTransform:
        system.metaTransform,

      buttonWeight:
        system.buttonWeight,

      buttonLetterSpacing:
        system.buttonLetterSpacing,

      buttonTransform:
        system.buttonTransform
    };
  }

  function resolveCustomSystem(
    data,
    customTypography
  ) {
    const headingFont =
      data.fontCatalog[
        customTypography.headingFont
      ];

    const bodyFont =
      data.fontCatalog[
        customTypography.bodyFont
      ];

    return {
      mode: 'custom',
      systemId: null,

      systemName:
        'Saját tipográfiai rendszer',

      categoryId:
        'custom',

      category:
        'Egyedi tipográfia',

      description:
        'Egyedileg összeállított címsor- és törzsszövegrendszer.',

      headingFont:
        customTypography.headingFont,

      headingFontLabel:
        headingFont?.label ||
        customTypography.headingFont,

      headingFontFamily:
        headingFont?.family ||
        'Arial, sans-serif',

      bodyFont:
        customTypography.bodyFont,

      bodyFontLabel:
        bodyFont?.label ||
        customTypography.bodyFont,

      bodyFontFamily:
        bodyFont?.family ||
        'Arial, sans-serif',

      headingWeight:
        numberValue(
          customTypography.headingWeight,
          700
        ),

      bodyWeight:
        numberValue(
          customTypography.bodyWeight,
          400
        ),

      headingLetterSpacing:
        numberValue(
          customTypography.headingLetterSpacing,
          -0.055
        ),

      headingLineHeight:
        numberValue(
          customTypography.headingLineHeight,
          0.94
        ),

      bodyLetterSpacing:
        numberValue(
          customTypography.bodyLetterSpacing,
          0
        ),

      bodyLineHeight:
        numberValue(
          customTypography.bodyLineHeight,
          1.65
        ),

      metaWeight:
        numberValue(
          customTypography.metaWeight,
          800
        ),

      metaLetterSpacing:
        numberValue(
          customTypography.metaLetterSpacing,
          0.12
        ),

      metaTransform:
        customTypography.metaTransform ||
        'uppercase',

      buttonWeight:
        numberValue(
          customTypography.buttonWeight,
          700
        ),

      buttonLetterSpacing:
        numberValue(
          customTypography.buttonLetterSpacing,
          0.01
        ),

      buttonTransform:
        customTypography.buttonTransform ||
        'none'
    };
  }

  function fontUrlFamilies(
    data,
    fontKeys
  ) {
    const uniqueFamilies = [];

    fontKeys.forEach((key) => {
      const family =
        data.fontCatalog[key]
          ?.googleFamily;

      if (
        family &&
        !uniqueFamilies.includes(family)
      ) {
        uniqueFamilies.push(
          family
        );
      }
    });

    return uniqueFamilies;
  }

  function ensureFonts(
    data,
    fontKeys
  ) {
    const families =
      fontUrlFamilies(
        data,
        fontKeys
      );

    if (!families.length) {
      return;
    }

    const signature =
      families
        .slice()
        .sort()
        .join('|');

    let hash = 0;

    for (
      let index = 0;
      index < signature.length;
      index += 1
    ) {
      hash =
        (
          hash * 31 +
          signature.charCodeAt(index)
        ) >>> 0;
    }

    const id =
      `${FONT_LINK_PREFIX}${hash}`;

    if (
      document.getElementById(id)
    ) {
      return;
    }

    const link =
      document.createElement('link');

    link.id = id;
    link.rel = 'stylesheet';

    link.href =
      `https://fonts.googleapis.com/css2?${families
        .map((family) => {
          return `family=${family}`;
        })
        .join('&')}&display=swap`;

    document.head.appendChild(
      link
    );
  }

  function initializeTypographyLabor(
    stage,
    data
  ) {
    if (
      !stage ||
      stage.getAttribute(
        INITIALIZED_ATTRIBUTE
      ) === 'true'
    ) {
      return;
    }

    stage.setAttribute(
      INITIALIZED_ATTRIBUTE,
      'true'
    );

    const section =
      stage.closest(
        '[data-zln-design-labor]'
      ) ||
      stage.closest(
        '.zln-design-labor-section'
      ) ||
      stage.parentElement;

    const state =
      loadTypographyState(data);

    const colors =
      getSelectedColors();

    const elements = {
      grid:
        stage.querySelector(
          '[data-typography-grid]'
        ),

      categoryIndex:
        stage.querySelector(
          '[data-typography-category-index]'
        ),

      categoryTitle:
        stage.querySelector(
          '[data-typography-category-title]'
        ),

      categoryDescription:
        stage.querySelector(
          '[data-typography-category-description]'
        ),

      pageDots:
        stage.querySelector(
          '[data-typography-page-dots]'
        ),

      prev:
        stage.querySelector(
          '[data-typography-page-prev]'
        ),

      next:
        stage.querySelector(
          '[data-typography-page-next]'
        ),

      mobilePrev:
        stage.querySelector(
          '[data-typography-mobile-prev]'
        ),

      mobileNext:
        stage.querySelector(
          '[data-typography-mobile-next]'
        ),

      mobileState:
        stage.querySelector(
          '[data-typography-mobile-state]'
        ),

      modeButtons:
        stage.querySelectorAll(
          '[data-typography-mode-button]'
        ),

      panels:
        stage.querySelectorAll(
          '[data-typography-panel]'
        ),

      selectionEmpty:
        stage.querySelector(
          '[data-typography-selection-empty]'
        ),

      selectionResult:
        stage.querySelector(
          '[data-typography-selection-result]'
        ),

      selectionMark:
        stage.querySelector(
          '[data-typography-selection-mark]'
        ),

      selectionName:
        stage.querySelector(
          '[data-typography-selection-name]'
        ),

      selectionMode:
        stage.querySelector(
          '[data-typography-selection-mode]'
        ),

      colorName:
        stage.querySelector(
          '[data-typography-color-name]'
        ),

      palettes:
        stage.querySelectorAll(
          '[data-placeholder-palette]'
        ),

      customFields:
        stage.querySelectorAll(
          '[data-custom-typography-field]'
        ),

      customValues:
        stage.querySelectorAll(
          '[data-custom-typography-value]'
        ),

      customPreview:
        stage.querySelector(
          '[data-custom-typography-preview]'
        ),

      customPair:
        stage.querySelector(
          '[data-custom-font-pair]'
        ),

      applyCustom:
        stage.querySelector(
          '[data-apply-custom-typography]'
        ),

      status:
        stage.querySelector(
          '[data-typography-status]'
        ),

      statusDot:
        stage.querySelector(
          '[data-typography-status-dot]'
        ),

      back:
        stage.querySelector(
          '[data-back-to-colors]'
        ),

      nextStep:
        stage.querySelector(
          '[data-typography-next]'
        )
    };

    applyColorContext(
      stage,
      elements,
      colors
    );

    populateFontSelects(
      elements,
      data,
      state
    );

    bindCustomControls(
      stage,
      elements,
      data,
      state
    );

    bindNavigation(
      stage,
      section,
      elements,
      data,
      state
    );

    switchMode(
      elements,
      state.typographyMode ||
        'presets'
    );

    renderCategory(
      stage,
      elements,
      data,
      state
    );

    updateSelection(
      stage,
      elements,
      data,
      state
    );

    updateCustomPreview(
      stage,
      elements,
      data,
      state
    );

    function applyColorContext(
      root,
      refs,
      palette
    ) {
      root.style.setProperty(
        '--zln-typo-bg',
        palette.background
      );

      root.style.setProperty(
        '--zln-typo-surface',
        palette.surface
      );

      root.style.setProperty(
        '--zln-typo-text',
        palette.text
      );

      root.style.setProperty(
        '--zln-typo-primary',
        palette.primary
      );

      root.style.setProperty(
        '--zln-typo-secondary',
        palette.secondary
      );

      if (refs.colorName) {
        refs.colorName.textContent =
          palette.paletteName;
      }

      const values = [
        palette.background,
        palette.surface,
        palette.text,
        palette.primary,
        palette.secondary
      ];

      refs.palettes.forEach(
        (paletteElement) => {
          paletteElement.innerHTML =
            values
              .map((color) => {
                return `
                  <span
                    style="background:${escapeHtml(color)}"
                  ></span>
                `;
              })
              .join('');
        }
      );
    }

    function populateFontSelects(
      refs,
      typographyData,
      currentState
    ) {
      const headingSelect =
        stage.querySelector(
          '[data-custom-typography-field="headingFont"]'
        );

      const bodySelect =
        stage.querySelector(
          '[data-custom-typography-field="bodyFont"]'
        );

      const options =
        Object.entries(
          typographyData.fontCatalog
        )
          .map(
            ([key, font]) => {
              return `
                <option
                  value="${escapeHtml(key)}"
                >
                  ${escapeHtml(font.label)}
                </option>
              `;
            }
          )
          .join('');

      if (headingSelect) {
        headingSelect.innerHTML =
          options;

        headingSelect.value =
          currentState
            .customTypography
            .headingFont;
      }

      if (bodySelect) {
        bodySelect.innerHTML =
          options;

        bodySelect.value =
          currentState
            .customTypography
            .bodyFont;
      }

      refs.customFields.forEach(
        (field) => {
          const key =
            field.dataset
              .customTypographyField;

          if (
            !(
              key in
              currentState.customTypography
            )
          ) {
            return;
          }

          field.value =
            String(
              currentState
                .customTypography[key]
            );
        }
      );
    }

    function renderCategory(
      root,
      refs,
      typographyData,
      currentState
    ) {
      const pageIndex =
        Math.max(
          0,
          Math.min(
            typographyData
              .categories.length - 1,

            numberValue(
              currentState.typographyPage,
              0
            )
          )
        );

      currentState.typographyPage =
        pageIndex;

      const category =
        typographyData.categories[
          pageIndex
        ];

      if (refs.categoryIndex) {
        refs.categoryIndex.textContent =
          `${String(
            pageIndex + 1
          ).padStart(
            2,
            '0'
          )} / ${String(
            typographyData
              .categories.length
          ).padStart(
            2,
            '0'
          )}`;
      }

      if (refs.categoryTitle) {
        refs.categoryTitle.textContent =
          category.title;
      }

      if (
        refs.categoryDescription
      ) {
        refs.categoryDescription.textContent =
          category.description;
      }

      if (refs.mobileState) {
        refs.mobileState.textContent =
          `${pageIndex + 1} / ${
            typographyData
              .categories.length
          }`;
      }

      if (refs.prev) {
        refs.prev.disabled =
          pageIndex === 0;
      }

      if (refs.mobilePrev) {
        refs.mobilePrev.disabled =
          pageIndex === 0;
      }

      const lastPage =
        pageIndex ===
        typographyData
          .categories.length - 1;

      if (refs.next) {
        refs.next.disabled =
          lastPage;
      }

      if (refs.mobileNext) {
        refs.mobileNext.disabled =
          lastPage;
      }

      renderDots(
        refs,
        typographyData,
        currentState
      );

      renderCards(
        root,
        refs,
        typographyData,
        currentState,
        category,
        pageIndex
      );

      saveTypographyState(
        currentState
      );
    }

    function renderDots(
      refs,
      typographyData,
      currentState
    ) {
      if (!refs.pageDots) {
        return;
      }

      refs.pageDots.innerHTML =
        '';

      typographyData.categories.forEach(
        (category, index) => {
          const button =
            document.createElement(
              'button'
            );

          button.type = 'button';

          button.className =
            `zln-typography-labor__page-dot${
              index ===
              currentState.typographyPage
                ? ' is-active'
                : ''
            }`;

          button.setAttribute(
            'aria-label',
            `${index + 1}. tipográfiai oldal: ${category.title}`
          );

          button.addEventListener(
            'click',
            () => {
              currentState.typographyPage =
                index;

              renderCategory(
                stage,
                refs,
                typographyData,
                currentState
              );
            }
          );

          refs.pageDots.appendChild(
            button
          );
        }
      );
    }

    function renderCards(
      root,
      refs,
      typographyData,
      currentState,
      category,
      pageIndex
    ) {
      if (!refs.grid) {
        return;
      }

      const fontKeys = [];

      category.systems.forEach(
        (system) => {
          fontKeys.push(
            system.headingFont,
            system.bodyFont
          );
        }
      );

      ensureFonts(
        typographyData,
        fontKeys
      );

      refs.grid.innerHTML = '';

      category.systems.forEach(
        (
          system,
          localIndex
        ) => {
          const resolved =
            resolveSystem(
              typographyData,
              system,
              category
            );

          const globalIndex =
            pageIndex * 10 +
            localIndex +
            1;

          const selected =
            currentState.typography
              ?.mode === 'preset' &&
            currentState.typography
              ?.systemId ===
              system.id;

          const button =
            document.createElement(
              'button'
            );

          button.type = 'button';

          button.className =
            `zln-typography-card${
              selected
                ? ' is-selected'
                : ''
            }`;

          button.setAttribute(
            'role',
            'listitem'
          );

          button.setAttribute(
            'aria-pressed',
            selected
              ? 'true'
              : 'false'
          );

          button.style.setProperty(
            '--card-heading-font',
            resolved.headingFontFamily
          );

          button.style.setProperty(
            '--card-body-font',
            resolved.bodyFontFamily
          );

          button.style.setProperty(
            '--card-heading-weight',
            resolved.headingWeight
          );

          button.style.setProperty(
            '--card-heading-spacing',
            formatEm(
              resolved
                .headingLetterSpacing
            )
          );

          button.style.setProperty(
            '--card-heading-line',
            formatLineHeight(
              resolved
                .headingLineHeight
            )
          );

          button.style.setProperty(
            '--card-body-weight',
            resolved.bodyWeight
          );

          button.style.setProperty(
            '--card-body-spacing',
            formatEm(
              resolved
                .bodyLetterSpacing
            )
          );

          button.style.setProperty(
            '--card-body-line',
            formatLineHeight(
              resolved.bodyLineHeight
            )
          );

          button.style.setProperty(
            '--card-meta-weight',
            resolved.metaWeight
          );

          button.style.setProperty(
            '--card-meta-spacing',
            formatEm(
              resolved
                .metaLetterSpacing
            )
          );

          button.style.setProperty(
            '--card-meta-transform',
            resolved.metaTransform
          );

          button.style.setProperty(
            '--card-button-weight',
            resolved.buttonWeight
          );

          button.style.setProperty(
            '--card-button-spacing',
            formatEm(
              resolved
                .buttonLetterSpacing
            )
          );

          button.style.setProperty(
            '--card-button-transform',
            resolved.buttonTransform
          );

          button.innerHTML = `
            <span
              class="zln-typography-card__meta-row"
            >
              <span>
                ${String(
                  globalIndex
                ).padStart(2, '0')}
              </span>

              <span
                class="zln-typography-card__selected"
              >
                Kiválasztva
              </span>
            </span>

            <span
              class="zln-typography-card__preview"
            >
              <span
                class="zln-typography-card__eyebrow"
              >
                ZLN / TYPE SYSTEM
              </span>

              <span
                class="zln-typography-card__headline"
              >
                Forma és karakter.
              </span>

              <span
                class="zln-typography-card__body"
              >
                A jó tipográfia ritmust,
                hierarchiát és felismerhető
                hangot ad a márkának.
              </span>

              <span
                class="zln-typography-card__button"
              >
                Felfedezem
              </span>
            </span>

            <span
              class="zln-typography-card__details"
            >
              <span
                class="zln-typography-card__name"
              >
                ${escapeHtml(
                  system.name
                )}
              </span>

              <span
                class="zln-typography-card__pair"
              >
                ${escapeHtml(
                  resolved
                    .headingFontLabel
                )}
                +
                ${escapeHtml(
                  resolved
                    .bodyFontLabel
                )}
              </span>

              <span
                class="zln-typography-card__description"
              >
                ${escapeHtml(
                  system.description
                )}
              </span>
            </span>
          `;

          button.addEventListener(
            'click',
            () => {
              currentState.typography =
                resolved;

              currentState.typographyMode =
                'presets';

              saveTypographyState(
                currentState
              );

              renderCategory(
                stage,
                refs,
                typographyData,
                currentState
              );

              updateSelection(
                stage,
                refs,
                typographyData,
                currentState
              );
            }
          );

          refs.grid.appendChild(
            button
          );
        }
      );
    }

    function switchMode(
      refs,
      mode
    ) {
      refs.modeButtons.forEach(
        (button) => {
          const active =
            button.dataset
              .typographyModeButton ===
            mode;

          button.classList.toggle(
            'is-active',
            active
          );

          button.setAttribute(
            'aria-selected',
            active
              ? 'true'
              : 'false'
          );
        }
      );

      refs.panels.forEach(
        (panel) => {
          panel.hidden =
            panel.dataset
              .typographyPanel !==
            mode;
        }
      );
    }

    function updateSelection(
      root,
      refs,
      typographyData,
      currentState
    ) {
      const selection =
        currentState.typography;

      if (!selection) {
        if (
          refs.selectionEmpty
        ) {
          refs.selectionEmpty.hidden =
            false;
        }

        if (
          refs.selectionResult
        ) {
          refs.selectionResult.hidden =
            true;
        }

        if (refs.status) {
          refs.status.textContent =
            'Válassz egy előre elkészített vagy saját tipográfiai rendszert.';
        }

        refs.statusDot
          ?.classList.remove(
            'is-complete'
          );

        if (refs.nextStep) {
          refs.nextStep.disabled =
            true;
        }

        return;
      }

      ensureFonts(
        typographyData,
        [
          selection.headingFont,
          selection.bodyFont
        ]
      );

      if (refs.selectionEmpty) {
        refs.selectionEmpty.hidden =
          true;
      }

      if (refs.selectionResult) {
        refs.selectionResult.hidden =
          false;
      }

      if (refs.selectionName) {
        refs.selectionName.textContent =
          selection.systemName;
      }

      if (refs.selectionMode) {
        refs.selectionMode.textContent =
          selection.mode ===
          'custom'
            ? 'Saját tipográfia'
            : selection.category;
      }

      if (refs.selectionMark) {
        refs.selectionMark.style.fontFamily =
          selection
            .headingFontFamily;

        refs.selectionMark.style.fontWeight =
          selection
            .headingWeight;
      }

      if (refs.status) {
        refs.status.textContent =
          `${selection.systemName} elmentve.`;
      }

      refs.statusDot
        ?.classList.add(
          'is-complete'
        );

      if (refs.nextStep) {
        refs.nextStep.disabled =
          false;
      }
    }

    function bindCustomControls(
      root,
      refs,
      typographyData,
      currentState
    ) {
      refs.customFields.forEach(
        (field) => {
          const key =
            field.dataset
              .customTypographyField;

          field.addEventListener(
            'input',
            () => {
              let value =
                field.value;

              if (
                field.type ===
                  'range' ||
                [
                  'headingWeight',
                  'bodyWeight'
                ].includes(key)
              ) {
                value =
                  numberValue(
                    value,
                    currentState
                      .customTypography[
                      key
                    ]
                  );
              }

              currentState
                .customTypography[
                key
              ] = value;

              currentState.typographyMode =
                'custom';

              saveTypographyState(
                currentState
              );

              updateCustomPreview(
                root,
                refs,
                typographyData,
                currentState
              );
            }
          );

          field.addEventListener(
            'change',
            () => {
              updateCustomPreview(
                root,
                refs,
                typographyData,
                currentState
              );
            }
          );
        }
      );

      refs.modeButtons.forEach(
        (button) => {
          button.addEventListener(
            'click',
            () => {
              const mode =
                button.dataset
                  .typographyModeButton;

              currentState.typographyMode =
                mode;

              saveTypographyState(
                currentState
              );

              switchMode(
                refs,
                mode
              );

              if (
                mode === 'custom'
              ) {
                updateCustomPreview(
                  root,
                  refs,
                  typographyData,
                  currentState
                );
              }
            }
          );
        }
      );

      refs.applyCustom
        ?.addEventListener(
          'click',
          () => {
            currentState.typography =
              resolveCustomSystem(
                typographyData,
                currentState
                  .customTypography
              );

            currentState.typographyMode =
              'custom';

            saveTypographyState(
              currentState
            );

            updateSelection(
              root,
              refs,
              typographyData,
              currentState
            );
          }
        );
    }

    function updateCustomPreview(
      root,
      refs,
      typographyData,
      currentState
    ) {
      const resolved =
        resolveCustomSystem(
          typographyData,
          currentState
            .customTypography
        );

      ensureFonts(
        typographyData,
        [
          resolved.headingFont,
          resolved.bodyFont
        ]
      );

      refs.customValues.forEach(
        (valueElement) => {
          const key =
            valueElement.dataset
              .customTypographyValue;

          const value =
            currentState
              .customTypography[
              key
            ];

          if (
            key
              .toLowerCase()
              .includes(
                'letterspacing'
              )
          ) {
            valueElement.textContent =
              formatEm(value);
          } else {
            valueElement.textContent =
              formatLineHeight(
                value
              );
          }
        }
      );

      if (refs.customPair) {
        refs.customPair.textContent =
          `${resolved.headingFontLabel} + ${resolved.bodyFontLabel}`;
      }

      if (
        !refs.customPreview
      ) {
        return;
      }

      refs.customPreview.style.setProperty(
        '--preview-heading-font',
        resolved.headingFontFamily
      );

      refs.customPreview.style.setProperty(
        '--preview-body-font',
        resolved.bodyFontFamily
      );

      refs.customPreview.style.setProperty(
        '--preview-heading-weight',
        resolved.headingWeight
      );

      refs.customPreview.style.setProperty(
        '--preview-heading-spacing',
        formatEm(
          resolved
            .headingLetterSpacing
        )
      );

      refs.customPreview.style.setProperty(
        '--preview-heading-line',
        formatLineHeight(
          resolved
            .headingLineHeight
        )
      );

      refs.customPreview.style.setProperty(
        '--preview-body-weight',
        resolved.bodyWeight
      );

      refs.customPreview.style.setProperty(
        '--preview-body-spacing',
        formatEm(
          resolved
            .bodyLetterSpacing
        )
      );

      refs.customPreview.style.setProperty(
        '--preview-body-line',
        formatLineHeight(
          resolved
            .bodyLineHeight
        )
      );

      refs.customPreview.style.setProperty(
        '--preview-meta-weight',
        resolved.metaWeight
      );

      refs.customPreview.style.setProperty(
        '--preview-meta-spacing',
        formatEm(
          resolved
            .metaLetterSpacing
        )
      );

      refs.customPreview.style.setProperty(
        '--preview-meta-transform',
        resolved.metaTransform
      );

      refs.customPreview.style.setProperty(
        '--preview-button-weight',
        resolved.buttonWeight
      );

      refs.customPreview.style.setProperty(
        '--preview-button-spacing',
        formatEm(
          resolved
            .buttonLetterSpacing
        )
      );

      refs.customPreview.style.setProperty(
        '--preview-button-transform',
        resolved.buttonTransform
      );
    }

    function bindNavigation(
      root,
      outerSection,
      refs,
      typographyData,
      currentState
    ) {
      refs.prev
        ?.addEventListener(
          'click',
          () => {
            if (
              currentState
                .typographyPage >
              0
            ) {
              currentState.typographyPage -=
                1;

              renderCategory(
                root,
                refs,
                typographyData,
                currentState
              );
            }
          }
        );

      refs.next
        ?.addEventListener(
          'click',
          () => {
            if (
              currentState
                .typographyPage <
              typographyData
                .categories.length -
                1
            ) {
              currentState.typographyPage +=
                1;

              renderCategory(
                root,
                refs,
                typographyData,
                currentState
              );
            }
          }
        );

      refs.mobilePrev
        ?.addEventListener(
          'click',
          () => {
            if (
              currentState
                .typographyPage >
              0
            ) {
              currentState.typographyPage -=
                1;

              renderCategory(
                root,
                refs,
                typographyData,
                currentState
              );

              scrollToCategory(
                root
              );
            }
          }
        );

      refs.mobileNext
        ?.addEventListener(
          'click',
          () => {
            if (
              currentState
                .typographyPage <
              typographyData
                .categories.length -
                1
            ) {
              currentState.typographyPage +=
                1;

              renderCategory(
                root,
                refs,
                typographyData,
                currentState
              );

              scrollToCategory(
                root
              );
            }
          }
        );

      refs.back
        ?.addEventListener(
          'click',
          () => {
            saveTypographyState(
              currentState
            );

            const colorButton =
              outerSection
                ?.querySelector(
                  '[data-labor-step-button="colors"]'
                );

            if (colorButton) {
              colorButton.click();

              window.setTimeout(
                () => {
                  mergeTypographyIntoMain(
                    currentState
                  );
                },
                0
              );

              return;
            }

            const colorStage =
              outerSection
                ?.querySelector(
                  '[data-labor-stage="colors"]'
                );

            if (colorStage) {
              root.hidden = true;
              colorStage.hidden =
                false;
            }
          }
        );

      refs.nextStep
        ?.addEventListener(
          'click',
          () => {
            if (
              !currentState.typography
            ) {
              return;
            }

            saveTypographyState(
              currentState
            );

            const headerStage =
              outerSection
                ?.querySelector(
                  '[data-labor-stage="header"]'
                );

            if (headerStage) {
              root.hidden = true;
              headerStage.hidden =
                false;

              document.dispatchEvent(
                new CustomEvent(
                  'zln:labor-step-changed',
                  {
                    detail: {
                      step: 'header'
                    }
                  }
                )
              );

              return;
            }

            if (refs.status) {
              refs.status.textContent =
                'A tipográfia elmentve. A fejléclabor lesz a következő lépés.';
            }
          }
        );
    }

    function scrollToCategory(
      root
    ) {
      root
        .querySelector(
          '.zln-typography-labor__category-header'
        )
        ?.scrollIntoView({
          behavior:
            window.matchMedia(
              '(prefers-reduced-motion: reduce)'
            ).matches
              ? 'auto'
              : 'smooth',

          block: 'start'
        });
    }
  }

  function initializeAll() {
    const data =
      window
        .ZLN_LABOR_TYPOGRAPHY_DATA;

    if (!data) {
      return;
    }

    document
      .querySelectorAll(
        '[data-zln-typography-labor]'
      )
      .forEach((stage) => {
        initializeTypographyLabor(
          stage,
          data
        );
      });
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const data =
        window
          .ZLN_LABOR_TYPOGRAPHY_DATA;

      if (!data) {
        return;
      }

      event.target
        .querySelectorAll(
          '[data-zln-typography-labor]'
        )
        .forEach((stage) => {
          initializeTypographyLabor(
            stage,
            data
          );
        });
    }
  );

  document.addEventListener(
    'zln:labor-typography-data-ready',
    initializeAll
  );

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      ensureTypographyData,
      {
        once: true
      }
    );
  } else {
    ensureTypographyData();
  }
})();